import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { useState } from 'react';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ok, err } from 'neverthrow';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { Assert } from '@cotera/utilities';
import { useLogger } from '@cotera/client/v0/actions/framework/logger';

type Params = {
  amount: number;
};

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

export class AddCreditsAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Add Credits';
  description = 'Add credits to your account';
  inputSchema = z.object({
    amount: z.number().min(0).max(500),
  });
  group = 'app';
  icon = 'credit-card';

  constructor(
    private readonly client: Pick<TenantedClient, 'billingSettings'>,
    private readonly queryClient: Pick<QueryClient, 'invalidateQueries'>,
    private readonly orgId: string,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    payload: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const response = await this.client.billingSettings.addCredits({
      amount: payload.amount,
    });

    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.settings.billing.credit_balance(this.orgId),
    });

    await this.queryClient.invalidateQueries({
      queryKey: queryKeys.settings.billing.billingSettingsInfo({
        orgId: this.orgId,
      }),
    });

    const success = response.isOk() && response.value.status === 'success';

    if (!success) {
      toast.error(
        'Failed to add credits. Check that your payment method is valid. If this problem persists, contact support.'
      );
      return err([
        {
          t: 'error',
          message: 'Failed to add credits',
        },
      ]);
    }

    toast.success('Credits successfully added. Balance will update shortly.');
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    return {
      event: CocoEvent.AddCreditsInitiated,
      properties: {},
    };
  }

  ask(
    _params: Partial<Params>,
    _context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <AddCreditsAskView
              client={this.client}
              orgId={this.orgId}
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }
}

const AddCreditsAskView: React.FC<{
  client: Pick<TenantedClient, 'billingSettings'>;
  orgId: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => void;
}> = ({ client, orgId, onRespond, onCancel }) => {
  const [amount, setAmount] = useState<number | ''>(0);
  const [loading, setLoading] = useState(false);
  const logger = useLogger();

  const { data: billingSettingsRes, isLoading: isLoadingBilling } = useQuery({
    queryKey: queryKeys.settings.billing.billingSettingsInfo({
      orgId,
    }),
    queryFn: () => client.billingSettings.getBillingSettingsInfo({}),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = amount === '' ? 0 : amount;
    if (value <= 0) {
      return;
    }
    setLoading(true);
    await onRespond({ amount: value });
    setLoading(false);
  };

  const billingInfo = billingSettingsRes?.isOk()
    ? billingSettingsRes.value.billingSettingsInfo
    : null;

  const canAddCC: boolean = (() => {
    if (billingInfo == null) {
      return false;
    }

    switch (billingInfo.kind) {
      case 'legacy-enterprise':
      case 'orb-enterprise':
        return false;

      case 'orb-business':
      case 'orb-pay-as-you-go':
        return billingInfo.orbInfo.creditCardInfo.kind === 'no-saved-card';

      default:
        return Assert.unreachable(billingInfo);
    }
  })();

  const handleClickAddCard = async () => {
    const addCCUrl = await client.billingSettings.generateAddCreditCardSession({
      returnUrl: window.location.href,
    });

    if (addCCUrl.isErr()) {
      logger.error('Error while generating add cc url');
      return;
    }

    window.location.href = addCCUrl.value.addCreditCardUrl;
  };

  if (isLoadingBilling) {
    return (
      <ModalPageView
        title="Add Credits"
        subtitle="Loading billing information…"
        className="px-4 py-4"
      >
        <div className="flex flex-col gap-4 w-full">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-coco-accent animate-pulse" />
            <div className="h-9 w-32 rounded-md bg-coco-accent animate-pulse" />
          </div>
          <div className="flex w-full justify-end gap-2">
            <div className="h-9 w-16 rounded-md bg-coco-accent animate-pulse" />
            <div className="h-9 w-24 rounded-md bg-coco-accent animate-pulse" />
          </div>
        </div>
      </ModalPageView>
    );
  }

  if (billingSettingsRes?.isErr() === true) {
    return (
      <ModalPageView
        title="Add Credits"
        subtitle="Unable to load billing information. Please try again."
        className="px-4 py-4"
      >
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => void onCancel()}>
            Cancel
          </Button>
        </div>
      </ModalPageView>
    );
  }

  if (
    billingInfo !== null &&
    (billingInfo.kind === 'legacy-enterprise' ||
      billingInfo.kind === 'orb-enterprise')
  ) {
    return (
      <ModalPageView
        title="Add Credits"
        subtitle="Add credits is not available for your plan. Contact support for billing assistance."
        className="px-4 py-4"
      >
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => void onCancel()}>
            Cancel
          </Button>
        </div>
      </ModalPageView>
    );
  }

  if (canAddCC) {
    return (
      <ModalPageView
        title="Add Credits"
        subtitle="Put your card in and get $20 of free credits"
        className="px-4 py-4"
      >
        <div className="flex flex-row gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            className="px-2 py-1"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleClickAddCard();
            }}
          >
            Add A Credit Card
          </Button>
        </div>
      </ModalPageView>
    );
  }

  return (
    <ModalPageView
      title="Add Credits"
      subtitle="Enter the dollar amount of credits to add to your account (max $500)"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit}>
        <div className="flex items-center gap-2">
          <span className="text-sm">$</span>
          <Input
            className="w-32 min-w-32"
            value={amount}
            type="number"
            min={0}
            max={500}
            onChange={(e) => {
              const value = e.target.value;
              setAmount(value === '' ? '' : Number(e.target.value));
            }}
          />
        </div>
        <div className="flex w-full justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="px-2 py-1"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              amount === '' ||
              (typeof amount === 'number' && amount <= 0) ||
              loading
            }
            className="px-2 py-1"
          >
            {loading ? (
              <>
                <Loading.Spinner variant="sm" /> Add Credits
              </>
            ) : (
              'Add Credits'
            )}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
